import React, { Component, ErrorInfo, ReactNode } from 'react';
import { analyticsService } from '../../lib/analytics/analyticsService';
import { logger } from '../../lib/logger';

interface Props {
  children: ReactNode;
  componentName?: string;
  sectionId?: string;
  sectionName?: string;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null
    };
  }

  static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      error
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // Log l'erreur
    logger.error(`Error in ${this.props.componentName || 'component'}`, {
      category: 'Error',
      data: {
        error,
        componentStack: errorInfo.componentStack,
        sectionId: this.props.sectionId,
        sectionName: this.props.sectionName
      }
    });

    // Envoyer l'erreur aux analytics
    analyticsService.trackEvent({
      event_type: 'error',
      event_name: 'component_error',
      event_data: {
        component_name: this.props.componentName || 'unknown',
        section_id: this.props.sectionId,
        section_name: this.props.sectionName,
        error_message: error.message,
        error_stack: error.stack,
        timestamp: new Date().toISOString()
      }
    });
  }

  render(): ReactNode {
    if (this.state.hasError) {
      return (
        <div className="flex flex-col items-center justify-center p-4 text-red-600">
          <h2 className="text-xl font-semibold mb-2">Une erreur est survenue</h2>
          <p className="text-center mb-4">
            {this.state.error?.message || 'Erreur inconnue'}
          </p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
          >
            Réessayer
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;